'use client';

import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Body } from '../typography/Body';
import { LoadingIndicator } from './LoadingIndicator';

import type { ComponentType, ReactNode } from 'react';

/**
 * Fallback size variants for lazy-loaded components
 * - full-page: Full viewport height with centered spinner
 * - section: Section-sized with padding and centered spinner
 * - inline: Inline spinner with minimal height
 */
export type LazyLoadFallbackSize = 'full-page' | 'section' | 'inline' | 'card';

interface LazyLoadProps<T extends ComponentType<unknown>> {
  /** Dynamic import function that returns the component */
  loader: () => Promise<{ default: T }>;
  /** Props to pass to the loaded component */
  componentProps?: T extends ComponentType<infer P> ? P : never;
  /** Size of the loading fallback */
  fallbackSize?: LazyLoadFallbackSize;
  /** Custom fallback component (overrides default spinner) */
  fallback?: ReactNode;
  /** Custom error fallback component */
  errorFallback?: ReactNode;
  /** Callback when component fails to load */
  onError?: (error: Error) => void;
}

/**
 * Wrapper component for lazy loading with consistent loading and error states
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LazyLoad
 *   loader={() => import('./HeavyComponent')}
 *   fallbackSize="section"
 * />
 *
 * // With props
 * <LazyLoad
 *   loader={() => import('./Chart')}
 *   componentProps={{ data: chartData, height: 400 }}
 *   fallbackSize="section"
 * />
 *
 * // With custom fallback
 * <LazyLoad
 *   loader={() => import('./Dashboard')}
 *   fallback={<CustomSkeleton />}
 * />
 * ```
 */
export function LazyLoad<T extends ComponentType<unknown>>({
  loader,
  componentProps,
  fallbackSize = 'section',
  fallback,
  errorFallback,
  onError,
}: LazyLoadProps<T>) {
  // Create lazy component
  const LazyComponent = lazy(loader);

  // Default loading fallback based on size
  const defaultFallback = fallback ?? <LoadingFallback size={fallbackSize} />;

  // Default error fallback
  const defaultErrorFallback = errorFallback ?? <ErrorFallback size={fallbackSize} />;

  return (
    <ErrorBoundary
      fallback={defaultErrorFallback}
      onError={(error) => {
        console.error('LazyLoad component failed:', error);
        onError?.(error instanceof Error ? error : new Error(String(error)));
      }}
    >
      <Suspense fallback={defaultFallback}>
        {/* @ts-expect-error - Generic component props spreading */}
        <LazyComponent {...componentProps} />
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * Loading fallback component with size variants
 */
function LoadingFallback({ size }: { size: LazyLoadFallbackSize }) {
  const containerStyles = {
    'full-page': 'min-h-screen',
    section: 'min-h-[400px] p-8',
    card: 'min-w-[200px] p-4',
    inline: 'min-h-[80px] py-4',
  };

  return (
    <div className={`flex items-center justify-center ${containerStyles[size]}`}>
      <div className="flex flex-col items-center gap-3">
        <LoadingIndicator />
        <Body className="text-content-tertiary" size="sm">
          Loading...
        </Body>
      </div>
    </div>
  );
}

/**
 * Error fallback component with size variants
 */
function ErrorFallback({ size }: { size: LazyLoadFallbackSize }) {
  const containerStyles = {
    'full-page': 'min-h-screen',
    section: 'min-h-[400px] p-8',
    card: 'min-w-[200px] p-4',
    inline: 'min-h-[80px] py-4',
  };

  return (
    <div className={`flex items-center justify-center ${containerStyles[size]}`}>
      <div className="flex flex-col items-center gap-2">
        <Body className="text-content-negative" size="md">
          Failed to load component
        </Body>
        <Body className="text-content-tertiary" size="sm">
          Please refresh the page to try again
        </Body>
      </div>
    </div>
  );
}

/**
 * Utility hook for creating lazy-loaded component wrappers
 *
 * @example
 * ```tsx
 * export const LazyChart = createLazyComponent(
 *   () => import('./Chart'),
 *   'section'
 * );
 * ```
 */
export function createLazyComponent<T extends ComponentType<unknown>>(
  loader: () => Promise<{ default: T }>,
  fallbackSize: LazyLoadFallbackSize = 'section'
) {
  return function LazyComponentWrapper(props: T extends ComponentType<infer P> ? P : never) {
    return <LazyLoad componentProps={props} fallbackSize={fallbackSize} loader={loader} />;
  };
}
